import { GtfsRoute } from '../types/gtfsTypes';
import { SecondsToHoursOptions, SecondsToHoursValue } from '../types/utilsTypes';

export interface DirectionVariantLabelSource {
    directionVariantName: string;
    directionVariantInternalId?: string | null;
    internalDirectionVariantId?: string | null;
    fromStopName?: string | null;
    firstStopName?: string | null;
    starts?: string | null;
    ends?: string | null;
    viaStopName?: string | null;
    toStopName?: string | null;
}

export default class Utils {
    static convertSecondsToMinutes(seconds: number | null, minutesLabel = '', secondsLabel = '', delimiter = ':'): string {
        if (seconds === null)
            return '';
        const minutesStr = `${Math.floor(Math.abs(seconds) / 60)}${minutesLabel}`;
        const secondsStr = `${('0' + Math.floor(Math.abs(seconds) % 60)).slice(-2)}${secondsLabel}`;
        const timeStr = `${minutesStr}${delimiter}${secondsStr}`;
        return seconds >= 0 ? timeStr : `-${timeStr}`;
    }
    static convertSecondsToHours(options: SecondsToHoursOptions): string {
        const { seconds, hoursLabel, minutesLabel, secondsLabel, delimiter, displaySeconds } = new SecondsToHoursValue(options);
        if (seconds === null)
            return '';
        const hoursStr = `${Math.floor(Math.abs(seconds) / 3600)}${hoursLabel}`;
        const remainingSeconds = Math.abs(seconds) % 3600;
        const minutesStr = `${delimiter}${('0' + Math.floor(Math.abs(remainingSeconds) / 60)).slice(-2)}${minutesLabel}`;
        const secondsStr = displaySeconds ? `${delimiter}${('0' + Math.floor(Math.abs(remainingSeconds) % 60)).slice(-2)}${secondsLabel}` : '';
        const timeStr = `${hoursStr}${minutesStr}${secondsStr}`;
        return seconds >= 0 ? timeStr : `-${timeStr}`;
    }
    static ensureIsArray<T>(couldAlreadyBeArray: T | T[]): T[] {
        return Array.isArray(couldAlreadyBeArray)
            ? couldAlreadyBeArray
            : [couldAlreadyBeArray];
    }
    static toQueryStringParams(params: Record<string, string>): string {
        const result = [] as string[];
        for (const param in params) {
            const value = params[param];
            result.push(`${param}=${value}`);
        }
        return result.join('&');
    }
    static removeDuplicatesByPropertyName(myArr: Array<Record<string, any>>, prop: string) {
        return myArr.filter((obj, pos, arr) => {
            return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
        });
    }
    static groupBy = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) =>
        list.reduce((previous, currentItem) => {
            const group = getKey(currentItem);
            if (!previous[group]) previous[group] = [];
            previous[group].push(currentItem);
            return previous;
        }, {} as Record<K, T[]>);
    static groupByEntries = <T, K extends keyof any>(list: T[], getKey: (item: T) => K) => Object.entries(Utils.groupBy(list, getKey)) as [K, T[]][];

    static utcToLocal(utcDateTimeString: string, minutesToAtz: number): Date {
        const minutesToMilliseconds = 60 * 1000;
        const lastIndex = utcDateTimeString.length - 1;
        if (utcDateTimeString[lastIndex] !== 'Z') {
            throw Error(`Expected date+time string (${utcDateTimeString}) in format 'yyyy-MM-ddTHH:mm:ssZ'`);
        }
        const browserDateTimeString = utcDateTimeString.substr(0, lastIndex);
        const atzDateTimeValue = Date.parse(browserDateTimeString) + minutesToAtz * minutesToMilliseconds;
        return new Date(atzDateTimeValue);
    }
    static roundNumber(value: number, roundDigits = 2) {
        const roundCoef = Math.pow(10, roundDigits);
        return Math.round((value + Number.EPSILON) * roundCoef) / roundCoef;
    }
    static kmToMiles(km: number, roundDigits = 2): number {
        const miles = km / 1.60934;
        return this.roundNumber(miles, roundDigits);
    }
    static kmToFeet(km: number, roundDigits = 2): number {
        const feet = km * 3280.84;
        return this.roundNumber(feet, roundDigits);
    }
    static getDistanceMeters(
        [lat1, lon1]: CoordinatePair,
        [lat2, lon2]: CoordinatePair,
    ): Meters {
        const R = 6371 as 6371 & Kilometers; // Radius of the earth
        const dLat = Utils.toRadiansForAngleInDegrees(lat2 - lat1 as Latitude);
        const dLon = Utils.toRadiansForAngleInDegrees(lon2 - lon1 as Longitude);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(Utils.toRadiansForAngleInDegrees(lat1)) *
            Math.cos(Utils.toRadiansForAngleInDegrees(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c as Kilometers;

        return Utils.toMetersFromKilometers(d);
    }
    static toRadiansForAngleInDegrees(deg: Degrees): Radians {
        return deg * (Math.PI / 180) as Radians;
    }
    static toMetersFromKilometers(km: Kilometers): Meters {
        return km * 1000 as Meters;
    }
    static distinctValuesByKey<T>(array: T[], key: string): T[] {
        return [...new Map(array.map(item => [(item as any)[key], item])).values()];
    }
    static getDirectionVariantName(otsTripShortName: string, tripHeadsign: string): string {
        return otsTripShortName ? `${otsTripShortName} to ${tripHeadsign}` : tripHeadsign;
    }

    static getDirectionVariantStableSortKey(variant: DirectionVariantLabelSource): string {
        const internalId = variant.directionVariantInternalId ?? variant.internalDirectionVariantId;
        if (internalId?.trim()) {
            return internalId.trim();
        }

        const origin = variant.fromStopName ?? variant.firstStopName;
        if (origin?.trim()) {
            return origin.trim();
        }

        if (variant.viaStopName?.trim()) {
            return variant.viaStopName.trim();
        }

        if (variant.toStopName?.trim()) {
            return variant.toStopName.trim();
        }

        return '';
    }

    static getDirectionVariantDuplicateCounter(
        variant: DirectionVariantLabelSource,
        group: DirectionVariantLabelSource[],
    ): string {
        const sorted = [...group].sort((a, b) =>
            Utils.getDirectionVariantStableSortKey(a).localeCompare(Utils.getDirectionVariantStableSortKey(b)),
        );
        const index = sorted.indexOf(variant);

        return `#${index + 1}`;
    }

    static parseTimeSpanToMinutes(value: string | null | undefined): number | null {
        if (!value?.trim()) {
            return null;
        }

        const trimmed = value.trim();
        let days = 0;
        let timePart = trimmed;
        const dotIndex = trimmed.indexOf('.');
        if (dotIndex > 0 && trimmed.indexOf(':') > dotIndex) {
            days = parseInt(trimmed.substring(0, dotIndex), 10) || 0;
            timePart = trimmed.substring(dotIndex + 1);
        }

        const segments = timePart.split(':');
        if (segments.length < 2) {
            return null;
        }

        const hours = parseInt(segments[0], 10);
        const minutes = parseInt(segments[1], 10);
        if (Number.isNaN(hours) || Number.isNaN(minutes)) {
            return null;
        }

        return days * 24 * 60 + hours * 60 + minutes;
    }

    static formatMinutesAsTimeLabel(totalMinutes: number): string {
        const normalizedMinutes = ((totalMinutes % (24 * 60)) + (24 * 60)) % (24 * 60);
        let hours = Math.floor(normalizedMinutes / 60);
        const minutes = normalizedMinutes % 60;
        const period = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        if (hours === 0) {
            hours = 12;
        }

        const minutePart = minutes === 0 ? '' : `:${minutes.toString().padStart(2, '0')}`;
        return `${hours}${minutePart}${period}`;
    }

    static formatGtfsTimeSpan(value: string | null | undefined): string | undefined {
        const totalMinutes = Utils.parseTimeSpanToMinutes(value);
        if (totalMinutes === null) {
            return undefined;
        }

        return Utils.formatMinutesAsTimeLabel(totalMinutes);
    }

    static formatServiceWindowLabel(starts: string | null | undefined, ends: string | null | undefined): string | undefined {
        const startLabel = Utils.formatGtfsTimeSpan(starts);
        const endLabel = Utils.formatGtfsTimeSpan(ends);
        if (!startLabel || !endLabel) {
            return undefined;
        }

        return `(${startLabel}–${endLabel})`;
    }

    static getDirectionVariantDisambiguator(
        variant: DirectionVariantLabelSource,
        duplicates: DirectionVariantLabelSource[],
    ): string | undefined {
        const windowLabel = Utils.formatServiceWindowLabel(variant.starts, variant.ends);

        if (!windowLabel) {
            if (duplicates.length <= 1) {
                return undefined;
            }

            return Utils.getDirectionVariantDuplicateCounter(variant, duplicates);
        }

        const sameWindow = duplicates.filter(
            d => Utils.formatServiceWindowLabel(d.starts, d.ends) === windowLabel,
        );

        if (sameWindow.length > 1) {
            const counter = Utils.getDirectionVariantDuplicateCounter(variant, sameWindow);
            return `${windowLabel} ${counter}`;
        }

        return windowLabel;
    }

    static getDirectionVariantDropdownLabel(
        variant: DirectionVariantLabelSource,
        siblings: DirectionVariantLabelSource[],
    ): string {
        const base = variant.directionVariantName;
        const duplicates = siblings.filter(s => s.directionVariantName === base);
        if (duplicates.length <= 1) {
            return base;
        }

        const disambiguator = Utils.getDirectionVariantDisambiguator(variant, duplicates);
        return disambiguator ? `${base} ${disambiguator}` : base;
    }

    static buildDirectionVariantDropdownOptions<T extends DirectionVariantLabelSource>(
        variants: T[],
        getValue: (variant: T) => string,
    ): { value: string; text: string }[] {
        return variants.map(variant => ({
            value: getValue(variant),
            text: Utils.getDirectionVariantDropdownLabel(variant, variants),
        }));
    }

    static enrichDirectionVariantsFromRoutes<T extends DirectionVariantLabelSource & { directionVariantInternalId?: string }>(
        variants: T[],
        routes: GtfsRoute[],
    ): T[] {
        const enrichmentByInternalId = new Map<string, DirectionVariantLabelSource>();
        for (const route of routes) {
            for (const directionVariant of route.directionVariants ?? []) {
                const internalId = directionVariant.internalDirectionVariantId;
                if (!internalId) {
                    continue;
                }

                enrichmentByInternalId.set(internalId, {
                    directionVariantName: directionVariant.directionVariantName,
                    fromStopName: directionVariant.fromStopName,
                    toStopName: directionVariant.toStopName,
                    starts: directionVariant.starts,
                    ends: directionVariant.ends,
                    viaStopName: directionVariant.viaStopName,
                });
            }
        }

        return variants.map(variant => {
            if (!variant.directionVariantInternalId) {
                return variant;
            }

            const enrichment = enrichmentByInternalId.get(variant.directionVariantInternalId);
            if (!enrichment) {
                return variant;
            }

            return {
                ...variant,
                fromStopName: variant.fromStopName ?? enrichment.fromStopName,
                toStopName: variant.toStopName ?? enrichment.toStopName,
                starts: variant.starts ?? enrichment.starts,
                ends: variant.ends ?? enrichment.ends,
                viaStopName: variant.viaStopName ?? enrichment.viaStopName,
            };
        });
    }

    static enrichDirectionVariantsWithOrigins<T extends DirectionVariantLabelSource & { directionVariantInternalId?: string }>(
        variants: T[],
        routes: GtfsRoute[],
    ): T[] {
        return Utils.enrichDirectionVariantsFromRoutes(variants, routes);
    }

}